import React, { type PropsWithChildren, useMemo } from 'react'
import { AIClient, AIExecuteClient, LocalStorageApiTokenProvider, MediaClient } from '@zocks-communications/client-sdk-js'
import { addBasicRefreshTokenInterceptor } from '../../../../api/client'
import { APIClientContext, type APIClientRegistry } from './APIClientContext'

export interface ClientProviderConfiguration {
  mediaApiBaseUrl: string
  aiApiBaseUrl: string
  aiExecuteApiBaseUrl: string
  trackingEnabled: boolean
  trackingApiBaseUrl: string
}

const API_TOKEN_KEY_NAME = 'authToken'

export const APIClientProvider: React.FC<PropsWithChildren<{ accountId: string; config: ClientProviderConfiguration }>> = ({ accountId, config, children }) => {
  const { mediaApiBaseUrl, aiApiBaseUrl, aiExecuteApiBaseUrl, trackingEnabled, trackingApiBaseUrl } = config

  const clients = useMemo(() => {
    const mediaClient = new MediaClient({
      accountId,
      apiBaseUrl: mediaApiBaseUrl,
      apiTokenProvider: [new LocalStorageApiTokenProvider({ apiTokenKeyName: API_TOKEN_KEY_NAME })],
    })
    const aiClient = new AIClient({
      accountId,
      aiApiBaseUrl,
      apiTokenProvider: [new LocalStorageApiTokenProvider({ apiTokenKeyName: API_TOKEN_KEY_NAME })],
    })
    const aiExecuteClient = new AIExecuteClient({
      accountId,
      aiExecuteApiBaseUrl,
      apiTokenProvider: [new LocalStorageApiTokenProvider({ apiTokenKeyName: API_TOKEN_KEY_NAME })],
    })
    addBasicRefreshTokenInterceptor(mediaClient.httpClient)
    addBasicRefreshTokenInterceptor(aiClient.httpClient)
    addBasicRefreshTokenInterceptor(aiExecuteClient.httpClient)
    return { mediaClient, aiClient, aiExecuteClient }
  }, [accountId, mediaApiBaseUrl, aiApiBaseUrl, aiExecuteApiBaseUrl])

  const registry = useMemo<APIClientRegistry>(() => ({ ...clients, trackingEnabled, trackingApiBaseUrl }), [clients, trackingEnabled, trackingApiBaseUrl])

  return <APIClientContext.Provider value={registry}>{children}</APIClientContext.Provider>
}
